import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useQuery, gql } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faHatWizard } from '@fortawesome/free-solid-svg-icons';
import { ThemeProvider } from 'styled-components';
import { theme } from '../../styles/theme';
import Button from '../Button/Button';
import TopNav from './TopNav';
import ChristmasMouse from '../ChristmasMouse/ChristmasMouse';
import AnnouncementBanner from '../common/AnnouncementBanner';
import { useBasket } from '../../contexts/BasketContext';
import { woocommerceService } from '../../services/woocommerceService';
import { SHOW_GLAMPING } from '../../config/stayFeatures';
import { isBookingOrTicketsPath } from '../../utils/bookingChrome';

const logo = 'https://BelvoirCastle.b-cdn.net/Logos/logo.webp';
const duchessLogo = 'https://BelvoirCastle.b-cdn.net/Logos/ducchessCollection.webp';
const farmshopLogo = 'https://BelvoirCastle.b-cdn.net/Logos/farmshopLogo.webp';

// GraphQL query to fetch the mega menu structure
const GET_MEGA_MENU = gql`
  query GetMegaMenu {
    siteSettings(where: {id: "site-settings"}) {
      megaMenu {
        sections {
          title
          path
          theme
          items {
            text
            path
            featured
            submenu {
              text
              path
            }
          }
        }
      }
    }
  }
`;

/** Ensure Featured Tours appears under Your Visit (after Tours) for castle nav — WP mega menu or fallback. */
function mergeFeaturedToursUnderYourVisit(items) {
  if (!items || !Array.isArray(items)) return items;
  return items.map((item) => {
    const visitPath = item.to || item.path;
    if (visitPath !== '/your-visit' || !Array.isArray(item.submenu)) return item;
    const hasFeatured = item.submenu.some(
      (s) => (s.to || s.path) === '/whats-on/featured-tours'
    );
    if (hasFeatured) return item;
    const newEntry = {
      to: '/whats-on/featured-tours',
      path: '/whats-on/featured-tours',
      text: 'Featured Tours'
    };
    const toursIndex = item.submenu.findIndex(
      (s) => (s.to || s.path) === '/your-visit/tours'
    );
    const newSubmenu = [...item.submenu];
    if (toursIndex >= 0) {
      newSubmenu.splice(toursIndex + 1, 0, newEntry);
    } else {
      newSubmenu.push(newEntry);
    }
    return { ...item, submenu: newSubmenu };
  });
}

/** Keep Accessibility near the top of Your Visit, whether the menu comes from WordPress or the fallback. */
function promoteAccessibilityUnderYourVisit(items) {
  if (!items || !Array.isArray(items)) return items;
  return items.map((item) => {
    const visitPath = item.to || item.path;
    if (visitPath !== '/your-visit' || !Array.isArray(item.submenu)) return item;
    const entry = {
      to: '/your-visit/accessibility',
      path: '/your-visit/accessibility',
      text: 'Accessibility'
    };
    const withoutExisting = item.submenu.filter(
      (s) => (s.to || s.path) !== '/your-visit/accessibility'
    );
    const dayTicketsIndex = withoutExisting.findIndex(
      (s) => (s.to || s.path) === '/your-visit/day-tickets'
    );
    const newSubmenu = [...withoutExisting];
    const insertAt = dayTicketsIndex >= 0 ? dayTicketsIndex + 1 : 0;
    newSubmenu.splice(insertAt, 0, entry);
    return { ...item, submenu: newSubmenu };
  });
}

const WHATS_ON_CHRISTMAS_LINK = {
  to: '/christmas',
  path: '/christmas',
  text: 'Christmas',
};

const WHATS_ON_HALLOWEEN_LINK = {
  to: '/halloween-belvoir-castle',
  path: '/halloween-belvoir-castle',
  text: "Halloween — Belvoir's Enchanted Halloween Trail",
};

const WHATS_ON_HALLOWEEN_CRAFTS_LINK = {
  to: '/halloween-crafts',
  path: '/halloween-crafts',
  text: 'Halloween Crafts',
};

const WHATS_ON_DOG_CLUB_LINK = {
  to: '/dog-club',
  path: '/dog-club',
  text: 'Dog Club',
};

const DOG_PARK_NAV_LINK = {
  to: '/dog-park',
  path: '/dog-park',
  text: 'Dog Park',
};

const WELLNESS_SITE_URL = 'https://www.belvoirwellness.com/';

const WELLNESS_NAV_LINK = {
  to: WELLNESS_SITE_URL,
  path: WELLNESS_SITE_URL,
  text: 'Wellness',
  external: true,
};

/** Point What's On → Belvoir Wellness at the wellness website. */
function pointBelvoirWellnessToSite(items) {
  if (!items || !Array.isArray(items)) return items;
  return items.map((item) => {
    if (!Array.isArray(item.submenu)) return item;
    const path = item.to || item.path;
    const isWhatsOn = path === '/whats-on' || (item.text || '').toLowerCase() === "what's on";
    if (!isWhatsOn) return item;
    return {
      ...item,
      submenu: item.submenu.map((sub) => {
        const subPath = sub.to || sub.path || '';
        const text = (sub.text || '').toLowerCase();
        const isWellness =
          text === 'belvoir wellness' ||
          subPath === '/whats-on/belvoir-wellness' ||
          subPath === '/wellness';
        if (!isWellness) return sub;
        return { ...sub, to: WELLNESS_SITE_URL, path: WELLNESS_SITE_URL, external: true };
      }),
    };
  });
}

/** Ensure Dog Park appears next to Animal Park in castle & retail nav — WP mega menu or fallback. */
function ensureDogParkNextToAnimalPark(items) {
  if (!items || !Array.isArray(items)) return items;
  const hasDogPark = items.some((item) => (item.to || item.path) === '/dog-park');
  if (hasDogPark) return items;
  const animalParkIndex = items.findIndex(
    (item) => (item.to || item.path) === '/retail-village/animal-park'
  );
  if (animalParkIndex < 0) return items;
  const next = [...items];
  next.splice(animalParkIndex + 1, 0, DOG_PARK_NAV_LINK);
  return next;
}

/** Place Wellness between Events and Getting Here on the Retail Village menu. */
function ensureWellnessBeforeGettingHere(items) {
  if (!items || !Array.isArray(items)) return items;
  const labels = items.map((item) => (item.text || '').toLowerCase());
  const isRetailMenu = labels.includes('shops') && labels.includes('events') && labels.includes('getting here');
  if (!isRetailMenu) return items;
  const hasWellness = items.some((item) => {
    const path = item.to || item.path || '';
    return (item.text || '').toLowerCase() === 'wellness' || path.includes('belvoirwellness.com');
  });
  if (hasWellness) return items;
  const gettingHereIndex = items.findIndex((item) => {
    const path = item.to || item.path || '';
    return (item.text || '').toLowerCase() === 'getting here' || path === '/retail-village/directions';
  });
  const next = [...items];
  if (gettingHereIndex >= 0) {
    next.splice(gettingHereIndex, 0, WELLNESS_NAV_LINK);
  } else {
    next.push(WELLNESS_NAV_LINK);
  }
  return next;
}

function isOutdoorLivingNavItem(item) {
  const path = item?.to || item?.path || '';
  const text = (item?.text || '').toLowerCase();
  return path.includes('outdoor-living-collective') || text.includes('outdoor living collective');
}

/** Remove Outdoor Living Collective from any menu — WP mega menu or fallback. */
function stripOutdoorLivingCollective(items) {
  if (!items || !Array.isArray(items)) return items;
  return items
    .filter((item) => !isOutdoorLivingNavItem(item))
    .map((item) => (
      Array.isArray(item.submenu)
        ? { ...item, submenu: stripOutdoorLivingCollective(item.submenu) }
        : item
    ));
}

function isSummerSafariNavItem(item) {
  const path = item?.to || item?.path || '';
  return path === '/belvoir-summer-safari' || path === '/summer-safari';
}

/** Remove Summer Safari from What's On — WP mega menu or fallback. */
function stripSummerSafariFromWhatsOn(items) {
  if (!items || !Array.isArray(items)) return items;
  return items.map((item) => {
    const whatsOnPath = item.to || item.path;
    if (whatsOnPath !== '/whats-on' || !Array.isArray(item.submenu)) return item;
    return { ...item, submenu: item.submenu.filter((s) => !isSummerSafariNavItem(s)) };
  });
}

/** Ensure Halloween appears under What's On — WP mega menu or fallback. */
function ensureHalloweenInWhatsOnSubmenu(items) {
  if (!items || !Array.isArray(items)) return items;
  return items.map((item) => {
    const whatsOnPath = item.to || item.path;
    if (whatsOnPath !== '/whats-on' || !Array.isArray(item.submenu)) return item;
    let submenu = [...item.submenu];
    const hasHalloween = submenu.some((s) => (s.to || s.path) === '/halloween-belvoir-castle');
    if (!hasHalloween) submenu = [...submenu, WHATS_ON_HALLOWEEN_LINK];
    const hasCrafts = submenu.some((s) => (s.to || s.path) === '/halloween-crafts');
    if (!hasCrafts) {
      const trailIndex = submenu.findIndex((s) => (s.to || s.path) === '/halloween-belvoir-castle');
      if (trailIndex >= 0) {
        submenu.splice(trailIndex + 1, 0, WHATS_ON_HALLOWEEN_CRAFTS_LINK);
      } else {
        submenu = [...submenu, WHATS_ON_HALLOWEEN_CRAFTS_LINK];
      }
    }
    return { ...item, submenu };
  });
}

/** Ensure Christmas appears under What's On (after Halloween) — WP mega menu or fallback. */
function ensureChristmasInWhatsOnSubmenu(items) {
  if (!items || !Array.isArray(items)) return items;
  return items.map((item) => {
    const whatsOnPath = item.to || item.path;
    if (whatsOnPath !== '/whats-on' || !Array.isArray(item.submenu)) return item;

    const existingIndex = item.submenu.findIndex((s) => (s.to || s.path) === '/christmas');
    const newSubmenu = [...item.submenu];
    const christmasEntry =
      existingIndex >= 0 ? newSubmenu.splice(existingIndex, 1)[0] : WHATS_ON_CHRISTMAS_LINK;

    const halloweenIndex = newSubmenu.findIndex(
      (s) => (s.to || s.path) === '/halloween-belvoir-castle'
    );
    const craftsIndex = newSubmenu.findIndex(
      (s) => (s.to || s.path) === '/halloween-crafts'
    );
    const insertAfter = craftsIndex >= 0 ? craftsIndex : halloweenIndex;
    if (insertAfter >= 0) {
      newSubmenu.splice(insertAfter + 1, 0, christmasEntry);
    } else {
      newSubmenu.push(christmasEntry);
    }
    return { ...item, submenu: newSubmenu };
  });
}

/** Ensure Dog Club appears under What's On (after Free Clubs) — WP mega menu or fallback. */
function ensureDogClubInWhatsOnSubmenu(items) {
  if (!items || !Array.isArray(items)) return items;
  return items.map((item) => {
    const whatsOnPath = item.to || item.path;
    if (whatsOnPath !== '/whats-on' || !Array.isArray(item.submenu)) return item;
    const hasDogClub = item.submenu.some((s) => (s.to || s.path) === '/dog-club');
    if (hasDogClub) return item;
    const freeClubsIndex = item.submenu.findIndex(
      (s) => (s.to || s.path) === '/whats-on/free-clubs-and-classes'
    );
    const newSubmenu = [...item.submenu];
    if (freeClubsIndex >= 0) {
      newSubmenu.splice(freeClubsIndex + 1, 0, WHATS_ON_DOG_CLUB_LINK);
    } else {
      newSubmenu.push(WHATS_ON_DOG_CLUB_LINK);
    }
    return { ...item, submenu: newSubmenu };
  });
}

const navConfig = {
  castle: [
    { to: "/your-visit", text: "Your Visit",
      submenu: [
        { to: "/your-visit/day-tickets", text: "Day Tickets" },
        { to: "/your-visit/accessibility", text: "Accessibility" },
        { to: "/your-visit/opening-times", text: "Opening Times" },
        { to: "/your-visit/directions", text: "Directions" },
        { to: "/your-visit/event-safety", text: "Event Safety" },
        { to: "/your-visit/tours", text: "Tours" },
        { to: "/whats-on/featured-tours", text: "Featured Tours" },
        { to: "/your-visit/groups", text: "Groups" }
      ]
    },
    { to: "/castle-dining", text: "Castle Dining", 
      submenu: [
        { to: "/castle-dining/aviary-tearoom", text: "The Aviary Tearoom" },
        { to: "/castle-dining/afternoon-tea", text: "Afternoon Tea" },
        { to: "/retail-village/dining", text: "Explore Retail Dining" },
        { to: "/about/gift-vouchers", text: "Gift Vouchers" }
      ] 
    },
    { to: "/whats-on", text: "What's On",
      submenu: [
        { to: "/whats-on/events", text: "Events" },
        { to: WELLNESS_SITE_URL, text: "Belvoir Wellness", external: true },
        { to: "/halloween-belvoir-castle", text: "Halloween — Belvoir's Enchanted Halloween Trail" },
        WHATS_ON_HALLOWEEN_CRAFTS_LINK,
        WHATS_ON_CHRISTMAS_LINK,
        { to: "/whats-on/featured-tours", text: "Featured Tours" },
        { to: "/whats-on/vineyard-tour", text: "Vineyard Tour" },
        { to: "/whats-on/harvest-day", text: "Harvest Day" },
        { to: "/whats-on/free-clubs-and-classes", text: "Free Clubs & Classes" },
        WHATS_ON_DOG_CLUB_LINK
      ]
    },
    { to: "/retail-village/animal-park", text: "Animal Park" },
    DOG_PARK_NAV_LINK,
    { to: "/private-hire", text: "Hire Now",
      submenu: [
        { to: "/weddings", text: "Weddings" },
        { to: "/private-hire", text: "Private Hire" }
      ]
    },
    { to: "/memberships", text: "Memberships",
      submenu: [
        { to: "/memberships", text: "Join Now" },
        { to: "/members-area", text: "Members Area" },
        { to: "/about/gift-vouchers", text: "Gift Vouchers" }
      ]
    },
    { to: "/journal", text: "Journal" },
    { to: "/about", text: "About",
      submenu: [
        { to: "/about/castle-and-state-rooms", text: "Castle & State Rooms" },
        { to: "/about/history", text: "History" },
        /* When mega menu is driven from WP Site Settings (GraphQL), add this link there too. */
        { to: "/about/belvoir-books", text: "Belvoir Books" },
        { to: "/about/gardens", text: "Gardens" },
        { to: "/about/adventure-playground", text: "Adventure Playground" },
        { to: "/about/careers", text: "Careers" },
        { to: "/about/parking", text: "Parking" },
        { to: "/about/dog-friendly", text: "Dog Friendly" }
      ]
    }
  ],
  retail: [
    { to: "/retail-village/shops", text: "Shops",
      submenu: [
        { to: "/retail-village/shops/farm-shop", text: "Farm Shop" },
        { to: "/retail-village/shops/duchess-collection", text: "Duchess Collection" },
        /* When mega menu is driven from WP Site Settings (GraphQL), add this link there too. */
        { to: "/retail-village/shops/belvoir-pet-shop", text: "Belvoir Pet Shop" }
      ]
    },
    { to: "/retail-village/animal-park", text: "Animal Park" },
    DOG_PARK_NAV_LINK,
    { to: "/retail-village/dining", text: "Dining",
      submenu: [
        { to: "/retail-village/dining/belvoir-bistro", text: "Belvoir Bistro" },
        { to: "/retail-village/dining/woodfired-pizza", text: "Belvoir Woodfired Pizza" },
        { to: "/retail-village/dining/balloon-bar", text: "Balloon Bar" },
        { to: "/castle-dining", text: "Explore Castle Dining" }
      ]
    },
    { to: "/whats-on/events", text: "Events" },
    WELLNESS_NAV_LINK,
    { to: "/retail-village/directions", text: "Getting Here" },
  ],
  stay: [
    { to: "/accommodations", text: "Accommodations",
      submenu: [
        { to: "/accommodations/vale-house", text: "Vale House" },
        { to: "/accommodations/cottages", text: "Cottages & Houses" },
        ...(SHOW_GLAMPING ? [{ to: "/accommodations/glamping", text: "Glamping" }] : []),
        { to: "/accommodations/camping", text: "Camping" }
      ]
    },
    { to: "/booking", text: "Book Now" },
    { to: "/experiences", text: "Experiences" },
    { to: "/getting-here", text: "Getting Here" }
  ],
  christmas: [
    { to: "/christmas", text: "Home" },
    { to: "/christmas/your-festive-visit", text: "Your Christmas Visit",
      submenu: [
        { to: "/christmas/your-festive-visit/faqs", text: "FAQs" }
      ]
    },
    { to: "/christmas/castle-by-candlelight", text: "Castle by Candlelight" },
    { to: "/christmas/mice-trail", text: "Mice Trail" },
    { to: "/christmas/festive-afternoon-tea", text: "Festive Afternoon Tea" },
    { to: "/christmas/santas-grotto-options", text: "Santa's Grotto",
      submenu: [
        { to: "/christmas/santas-grotto-castle", text: "Castle" },
        { to: "/christmas/santas-grotto-retail-village-options", text: "Retail Village" }
      ]
    },
    { to: "/christmas/search-for-santa", text: "Search for Santa" }
  ],
  duchess: [
    { to: "/duchess-collection", text: "Shop" },
    { to: "/duchess-collection/checkout", text: "Checkout" }
  ],
  farmshop: [
    { to: "/farm-shop", text: "Shop" },
    { to: "/retail-village/shops/farm-shop", text: "About" }
  ],
};

const sectionThemes = {
  castle: {
    navBackground: theme.colors.offWhite,
    linkColor: theme.colors.primary,
    dropdownBackground: '#f9f5f0',
  },
  retail: {
    navBackground: theme.colors.offWhite,
    linkColor: theme.colors.primary,
    dropdownBackground: '#f9f5f0',
  },
  christmas: {
    navBackground: theme.colors.offWhite,
    linkColor: theme.colors.primary,
    dropdownBackground: '#f9f5f0',
  },
  duchess: {
    navBackground: theme.colors.offWhite,
    linkColor: theme.colors.primary,
    dropdownBackground: '#f9f5f0',
  },
  farmshop: {
    navBackground: theme.colors.offWhite,
    linkColor: theme.colors.primary,
    dropdownBackground: '#f9f5f0',
  }
};

const NavContainer = styled.nav`
  background-color: ${props => {
    if (props.$scrolled) {
      return 'rgba(248, 247, 244, 0.94)';
    }
    return sectionThemes[props.$currentSection]?.navBackground || props.theme.colors.offWhite;
  }};
  padding: 0.875rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: ${props =>
    props.$scrolled
      ? '0 4px 28px rgba(16, 29, 64, 0.1)'
      : '0 1px 0 rgba(16, 29, 64, 0.06)'};
  border-bottom: 1px solid ${props =>
    props.$scrolled ? 'rgba(16, 29, 64, 0.06)' : 'transparent'};
  position: relative;
  z-index: 3;
  /* When the slide-out menu is open, stack above TopNav (z-index: 5) so overlay + drawer cover the full header */
  ${props =>
    props.$menuOpen &&
    `
    @media (max-width: 1024px) {
      z-index: 50;
    }
  `}
  transition: background-color 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;

  ${props =>
    props.$scrolled &&
    `
    backdrop-filter: blur(18px) saturate(160%);
    -webkit-backdrop-filter: blur(18px) saturate(160%);
  `}

  @media (prefers-reduced-transparency: reduce) {
    ${props =>
      props.$scrolled &&
      `
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    `}
  }

  @media (min-width: 1440px) {
    padding: 0.875rem 8rem;
  }

  @media (min-width: 1024px) and (max-width: 1439px) {
    padding: 0.875rem 4rem;
  }
`;

const Logo = styled.img`
  height: 52px;
  display: block;

  @media (max-width: 768px) {
    height: 44px;
  }
`;

/** Above the dimmed overlay, and below the slide-out menu so it does not cover the first link. */
const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    position: relative;
    z-index: 1000;
  }
`;

const MobileMenuHeader = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    width: 100%;
    padding-bottom: 1rem;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid rgba(16, 29, 64, 0.1);
  }
`;

const MobileMenuTitle = styled.p`
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.42);
`;

const NavLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  min-height: 60px;

  @media (min-width: 1440px) {
    gap: 1.5rem;
  }

  @media (min-width: 1024px) and (max-width: 1439px) {
    gap: 1rem;
  }

  @media (max-width: 1024px) {
    position: fixed;
    flex-direction: column;
    background: ${props => props.theme.colors.offWhite};
    top: 0;
    right: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
    height: 100vh;
    height: 100dvh;
    width: min(340px, 92vw);
    padding: calc(1.1rem + env(safe-area-inset-top, 0px)) 1.25rem
      calc(1.5rem + env(safe-area-inset-bottom, 0px));
    gap: 0.35rem;
    transition: right 0.32s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: -12px 0 48px rgba(16, 29, 64, 0.14);
    z-index: 1000;
    align-items: stretch;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }
`;

const NavItem = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;

  &:hover .dropdown-menu {
    display: block;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 1024px) {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    align-content: flex-start;
    height: auto;
  }
`;

const NavLink = styled(Link)`
  color: ${props => sectionThemes[props.$currentSection]?.linkColor || props.theme.colors.primary};
  text-decoration: none;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(0.9375rem, 0.9rem + 0.2vw, 1.0625rem);
  font-weight: 600;
  padding: 0.35rem 0 0.45rem;
  display: flex;
  align-items: center;
  white-space: nowrap;
  letter-spacing: 0.02em;
  transition: color 0.2s ease, opacity 0.2s ease;

  @media (hover: hover) {
    &:hover {
      opacity: 0.88;
    }
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 4px;
    border-radius: 2px;
  }

  @media (max-width: 1024px) {
    white-space: normal;
    flex: 1;
    min-width: 0;
    min-height: 48px;
    padding: 0.75rem 0.5rem 0.75rem 0;
    box-sizing: border-box;
    align-items: center;
  }
`;

const DropdownMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: ${props => sectionThemes[props.$currentSection]?.dropdownBackground || '#f9f5f0'};
  min-width: 200px;
  box-shadow: 0 16px 48px rgba(16, 29, 64, 0.12);
  z-index: 1000;
  padding: 0.65rem 0;
  border-radius: 12px;
  border: 1px solid rgba(16, 29, 64, 0.06);
  border-top: 2px solid ${props => props.theme.colors.accent};

  @media (min-width: 1440px) {
    min-width: 232px;
  }

  @media (max-width: 1024px) {
    position: static;
    box-shadow: none;
    flex-basis: 100%;
    width: 100%;
    padding: ${props => props.$isOpen ? '0.35rem 0 0.65rem 0.25rem' : '0'};
    max-height: ${props => props.$isOpen ? '500px' : '0'};
    overflow: hidden;
    transition: all 0.3s ease;
    display: block;
    opacity: ${props => props.$isOpen ? '1' : '0'};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    background-color: transparent;
    border-top: none;
  }
`;

const DropdownLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(0.9375rem, 0.92rem + 0.15vw, 1.05rem);
  padding: 0.65rem 1.35rem;
  display: block;
  transition: background 0.2s ease, padding 0.2s ease, border-color 0.2s ease;
  border-left: 3px solid transparent;

  &:hover {
    background-color: rgba(16, 29, 64, 0.05);
    border-left-color: ${props => props.theme.colors.accent};
    padding-left: 1.45rem;
  }

  &:focus-visible {
    outline: none;
    background-color: rgba(157, 109, 73, 0.12);
    border-left-color: ${props => props.theme.colors.accent};
  }

  @media (max-width: 1024px) {
    padding: 0.5rem 0;
    border-left: none;
    
    &:hover {
      border-left: none;
      padding-left: 0;
    }
  }
`;

const StyledButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 0.75rem 1.35rem;
  margin-left: 0.75rem;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(157, 109, 73, 0.28);

  @media (min-width: 1440px) {
    padding: 0.8rem 1.5rem;
    margin-left: 1rem;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
    text-align: center;
  }
`;

const BasketButtonNav = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.75rem 1.25rem;
  margin-left: 0.75rem;
  color: white;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(157, 109, 73, 0.28);
  transition: transform 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 3px;
  }

  @media (min-width: 1440px) {
    margin-left: 1rem;
    padding: 0.8rem 1.4rem;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
  }
`;

const BasketCountBubble = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  width: 22px;
  height: 22px;
  border-radius: 50%;
`;

// Duchess search in navbar
const DuchessSearchForm = styled.form`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;

  @media (max-width: 1024px) {
    width: 100%;
    margin-left: 0;
  }
`;

const DuchessSearchInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 16px;
  font-size: 0.9rem;
  outline: none;
  width: 200px;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const DuchessSearchButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 16px;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(0.95);
  }
`;

const Hamburger = styled.button`
  display: none;
  cursor: pointer;
  z-index: 1001;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0 0.5rem;
  min-width: 44px;
  min-height: 44px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid rgba(16, 29, 64, 0.11);
  /* Opaque fill so the drawer’s submenu chevrons (same top-right band) never show through */
  background: ${props => props.theme.colors.offWhite};
  isolation: isolate;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }

  @media (hover: hover) {
    &:hover {
      background: #f0eee9;
      border-color: rgba(16, 29, 64, 0.16);
    }
  }

  &:active {
    background: #e9e6e0;
  }

  ${props =>
    props.$isOpen &&
    `
    background: ${props.theme.colors.offWhite};
    border-color: rgba(16, 29, 64, 0.2);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
  `}

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    justify-content: center;
  }

  span {
    display: block;
    width: 22px;
    height: 2px;
    border-radius: 1px;
    background-color: rgba(16, 29, 64, 0.88);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;

    &:first-child {
      transform: ${({ $isOpen }) =>
        $isOpen ? 'translateY(6px) rotate(45deg)' : 'translateY(0) rotate(0)'};
    }

    &:nth-child(2) {
      opacity: ${({ $isOpen }) => ($isOpen ? '0' : '1')};
      visibility: ${({ $isOpen }) => ($isOpen ? 'hidden' : 'visible')};
      transform: ${({ $isOpen }) => ($isOpen ? 'scaleX(0)' : 'none')};
    }

    &:last-child {
      transform: ${({ $isOpen }) =>
        $isOpen ? 'translateY(-6px) rotate(-45deg)' : 'translateY(0) rotate(0)'};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      transition-duration: 0.01ms;
    }
  }
`;

const Overlay = styled.div`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  
  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(16, 29, 64, 0.42);
    z-index: 999;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);

    @media (prefers-reduced-transparency: reduce) {
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      background-color: rgba(16, 29, 64, 0.55);
    }
  }
`;

const MobileDropdownToggle = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 0.5rem;
  color: ${props => props.theme.colors.primary};
  position: relative;
  padding: 0.35rem;
  border-radius: 6px;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }

  @media (max-width: 1024px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    min-height: 44px;
    flex-shrink: 0;
    margin-left: 0;
    
    &::before {
      content: '>';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) ${props => props.isOpen ? 'rotate(-90deg)' : 'rotate(90deg)'};
      transition: transform 0.3s ease;
      font-size: 14px;
      font-weight: bold;
    }
  }
`;

// ── SUMMER SAFARI NAV TEASER ─────────────────────────────────────────────────

const SAFARI_TAG_RESERVE = '56px';

const NavbarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  /* Desktop only: reserve space for the hanging safari tag */
  padding-bottom: ${props => (props.$hasSafariTag ? SAFARI_TAG_RESERVE : '0')};
  transition: padding-bottom 0.25s ease;

  @media (max-width: 768px) {
    padding-bottom: 0;
  }
`;

const safariTagDrop = keyframes`
  0%   { transform: translateY(-115%); opacity: 0; }
  60%  { transform: translateY(6px); opacity: 1; }
  80%  { transform: translateY(-2px); }
  100% { transform: translateY(0); opacity: 1; }
`;

const safariTagSway = keyframes`
  0%, 100% { transform: rotate(0deg); }
  30%      { transform: rotate(1.6deg); }
  65%      { transform: rotate(-1.2deg); }
`;

const SafariTagWrap = styled.div`
  position: absolute;
  /* Desktop: offset upward — NavbarContainer padding-bottom extends height, so plain top:100% drops too far */
  top: calc(100% - ${SAFARI_TAG_RESERVE});
  right: clamp(1rem, 6vw, 8rem);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${safariTagDrop} 0.9s cubic-bezier(0.34, 1.4, 0.64, 1) 2.2s both;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0.01ms;
    animation-delay: 0s;
  }

  /* Mobile: sit in the header stack so it never overlaps page content */
  @media (max-width: 768px) {
    position: relative;
    top: auto;
    right: auto;
    align-self: center;
    width: 100%;
    justify-content: center;
    margin: 0.35rem 0 0.45rem;
    animation: none;
    pointer-events: auto;
  }
`;

const SafariTagString = styled.span`
  display: block;
  width: 1px;
  height: 14px;
  background: rgba(80, 30, 100, 0.45);

  @media (max-width: 768px) {
    display: none;
  }
`;

const SafariTagBody = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.42rem 0.85rem;
  border: 1px solid rgba(180, 120, 220, 0.35);
  border-radius: 999px;
  background: linear-gradient(135deg, #2d0a3e 0%, #5c1a7a 100%);
  color: #e8c8ff;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(80, 30, 120, 0.35);
  transform-origin: top center;
  animation: ${safariTagSway} 6s ease-in-out 3.2s infinite;
  transition: box-shadow 0.2s ease, filter 0.2s ease;

  &:hover {
    filter: brightness(1.08);
    box-shadow: 0 8px 24px rgba(80, 30, 120, 0.5);
    animation-play-state: paused;
  }

  &:focus-visible {
    outline: 2px solid #b47adc;
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 600px) {
    font-size: 0.6rem;
    padding: 0.38rem 0.7rem;
    white-space: normal;
    text-align: center;
    max-width: min(100%, 20rem);
  }
`;

const SafariTagPaw = styled.span`
  font-size: 0.8rem;
  line-height: 1;
`;

const SafariTagDismiss = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.15rem;
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1;
  color: rgba(232, 200, 255, 0.55);
  cursor: pointer;

  &:hover {
    color: #e8c8ff;
  }
`;

// ── END SUMMER SAFARI NAV TEASER ─────────────────────────────────────────────

const NavDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
  z-index: 1000;
  
  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const NavDropdownItem = styled(Link)`
  display: block;
  padding: 0.8rem 1.5rem;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.greyBackground};
    color: ${props => props.theme.colors.accent};
  }
`;

const Navbar = () => {
  const mouseLogoRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [currentSection, setCurrentSection] = useState('castle');
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [duchessCategories, setDuchessCategories] = useState([]);
  const [duchessSearchTerm, setDuchessSearchTerm] = useState('');
  const [farmshopSearchTerm, setFarmshopSearchTerm] = useState('');
  const [safariTeaserDismissed, setSafariTeaserDismissed] = useState(
    () => sessionStorage.getItem('safariTeaserDismissed') === '1'
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(GET_MEGA_MENU);
  const { totalItems } = useBasket();

  useEffect(() => {
    // Listen for custom events to force section
    const handleForceRetailSection = (event) => {
      if (event.detail && event.detail.section === 'retail') {
        setCurrentSection('retail');
      }
    };

    // Add event listeners
    document.addEventListener('forceRetailSection', handleForceRetailSection);

    // Set initial section based on path
    if (location.pathname.startsWith('/duchess-collection')) {
      setCurrentSection('duchess');
    } else if (location.pathname.startsWith('/farm-shop')) {
      setCurrentSection('farmshop');
    } else if (location.pathname.startsWith('/christmas')) {
      setCurrentSection('christmas');
    } else if (location.pathname.includes('retail-village')) {
      setCurrentSection('retail');
    } else {
      setCurrentSection('castle');
    }

    // Clean up
    return () => {
      document.removeEventListener('forceRetailSection', handleForceRetailSection);
    };
  }, [location]);

  // Fetch WooCommerce categories for Duchess menu
  useEffect(() => {
    const fetchCats = async () => {
      if (currentSection === 'duchess') {
        try {
          const cats = await woocommerceService.getCategories({ per_page: 100, parent: 0, orderby: 'name', order: 'asc' });
          setDuchessCategories(cats || []);
        } catch (e) {

          setDuchessCategories([]);
        }
      }
    };
    fetchCats();
  }, [currentSection]);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 36);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const toggleDropdown = (index) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Use WordPress data if available, otherwise use hardcoded config
  const rawMenuItems = currentSection === 'duchess'
    ? [
        { to: '/duchess-collection?sort=date', text: 'New Arrivals' },
        { to: '/duchess-collection?sale=1', text: 'Sale' },
        { text: 'Categories', submenu: duchessCategories.map(c => ({ to: `/duchess-collection?category=${c.id}`, text: c.name })) },
        { to: '/retail-village/shops/duchess-collection', text: 'About' }
      ]
    : (data?.siteSettings?.megaMenu?.sections?.find(
        section => section.theme === currentSection
      )?.items || navConfig[currentSection]);

  const menuItems = currentSection === 'duchess'
    ? rawMenuItems
    : stripOutdoorLivingCollective(
        pointBelvoirWellnessToSite(
        ensureWellnessBeforeGettingHere(
        ensureDogParkNextToAnimalPark(
          ensureDogClubInWhatsOnSubmenu(
            ensureChristmasInWhatsOnSubmenu(
              ensureHalloweenInWhatsOnSubmenu(
                stripSummerSafariFromWhatsOn(
                  promoteAccessibilityUnderYourVisit(mergeFeaturedToursUnderYourVisit(rawMenuItems))
                )
              )
            )
          )
        )
        )
        )
      );

  const showSafariTag =
    currentSection === 'castle' &&
    !safariTeaserDismissed &&
    !isOpen &&
    !location.pathname.includes('/belvoir-summer-safari') &&
    !location.pathname.includes('/summer-safari') &&
    !location.pathname.includes('/halloween-belvoir-castle') &&
    !location.pathname.includes('/halloween-crafts') &&
    !location.pathname.includes('/belvoir-wicked-night') &&
    !location.pathname.includes('/belvoir-wellness') &&
    location.pathname !== '/wellness' &&
    !location.pathname.startsWith('/explore') &&
    !isBookingOrTicketsPath(location.pathname);

  return (
    <ThemeProvider theme={theme}>
      <NavbarContainer id="site-header" $hasSafariTag={showSafariTag}>
        <TopNav 
          currentSection={(currentSection === 'duchess' || currentSection === 'farmshop') ? 'retail' : currentSection} 
          onSectionChange={setCurrentSection}
          compact={currentSection === 'duchess' || currentSection === 'farmshop'}
          navScrolled={navScrolled}
        />
        <NavContainer $currentSection={currentSection} $scrolled={navScrolled} $menuOpen={isOpen}>
          <LogoLink ref={mouseLogoRef} to={currentSection === 'duchess' ? '/duchess-collection' : currentSection === 'farmshop' ? '/farm-shop' : '/'}>
            {currentSection === 'duchess' ? (
              <Logo src={duchessLogo} alt="The Duchess Collection Logo" />
            ) : currentSection === 'farmshop' ? (
              <Logo src={farmshopLogo} alt="Belvoir Farm Shop Logo" />
            ) : (
              <Logo src={logo} alt="Belvoir Castle Logo" />
            )}
          </LogoLink>
          <Hamburger
            type="button"
            $isOpen={isOpen}
            onClick={toggleMenu}
            aria-expanded={isOpen}
            aria-controls="primary-navigation"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </Hamburger>
          <Overlay $isOpen={isOpen} onClick={closeMenu} />
          <NavLinks
            id="primary-navigation"
            $isOpen={isOpen}
            aria-label="Primary navigation"
          >
            <MobileMenuHeader>
              <MobileMenuTitle>Menu</MobileMenuTitle>
            </MobileMenuHeader>
            {menuItems.map((item, index) => (
              <NavItem key={item.to || item.path}>
                {item.external ? (
                  <NavLink
                    as="a"
                    href={item.to || item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                    $currentSection={currentSection}
                  >
                    {item.text}
                  </NavLink>
                ) : (
                <NavLink 
                  to={item.to || item.path} 
                  onClick={item.submenu ? undefined : closeMenu}
                  $currentSection={currentSection}
                >
                  {item.text}
                </NavLink>
                )}
                
                {item.submenu && (
                  <>
                    <MobileDropdownToggle 
                      type="button"
                      onClick={() => toggleDropdown(index)}
                      isOpen={openDropdowns[index]}
                      aria-expanded={!!openDropdowns[index]}
                      aria-label={`${openDropdowns[index] ? 'Collapse' : 'Expand'} ${item.text} submenu`}
                    />
                    <DropdownMenu 
                      className="dropdown-menu" 
                      $currentSection={currentSection}
                      $isOpen={openDropdowns[index]}
                    >
                      {item.submenu.map(subItem => (
                        subItem.external ? (
                          <DropdownLink
                            as="a"
                            key={subItem.to || subItem.path}
                            href={subItem.to || subItem.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeMenu}
                            $currentSection={currentSection}
                          >
                            {subItem.text}
                          </DropdownLink>
                        ) : (
                        <DropdownLink 
                          key={subItem.to || subItem.path} 
                          to={subItem.to || subItem.path} 
                          onClick={closeMenu}
                          $currentSection={currentSection}
                        >
                          {subItem.text}
                        </DropdownLink>
                        )
                      ))}
                    </DropdownMenu>
                  </>
                )}
              </NavItem>
            ))}
            {(currentSection === 'duchess' || currentSection === 'farmshop') && (
              <DuchessSearchForm onSubmit={(e) => { e.preventDefault(); closeMenu(); navigate(`/duchess-collection?view=all&q=${encodeURIComponent(duchessSearchTerm)}`); }}>
                {currentSection === 'duchess' ? (
                  <>
                    <DuchessSearchInput 
                      type="search" 
                      placeholder="Search shop..." 
                      value={duchessSearchTerm}
                      onChange={(e) => setDuchessSearchTerm(e.target.value)}
                    />
                    <DuchessSearchButton type="submit">Search</DuchessSearchButton>
                  </>
                ) : (
                  <>
                    <DuchessSearchInput 
                      type="search" 
                      placeholder="Search farm shop..." 
                      value={farmshopSearchTerm}
                      onChange={(e) => setFarmshopSearchTerm(e.target.value)}
                    />
                    <DuchessSearchButton type="submit" onClick={(e) => { e.preventDefault(); closeMenu(); navigate(`/farm-shop?view=all&q=${encodeURIComponent(farmshopSearchTerm)}`); }}>Search</DuchessSearchButton>
                  </>
                )}
              </DuchessSearchForm>
            )}
            {(currentSection === 'duchess' || currentSection === 'farmshop') ? (
              <BasketButtonNav to="#" onClick={(e) => { e.preventDefault(); closeMenu(); document.dispatchEvent(new Event('openBasketSidebar')); }}>
                <FontAwesomeIcon icon={faShoppingCart} />
                Basket
                {totalItems > 0 && (
                  <BasketCountBubble>{totalItems}</BasketCountBubble>
                )}
              </BasketButtonNav>
            ) : (
              <StyledButton
                as={Link}
                to={currentSection === 'christmas' ? '/christmas/tickets' : '/your-visit/day-tickets'}
                onClick={closeMenu}
              >
                TICKETS
              </StyledButton>
            )}
          </NavLinks>
        </NavContainer>
        <AnnouncementBanner />

        {/* Halloween tease tag — hangs below the navbar */}
        {showSafariTag && (
          <SafariTagWrap>
            <SafariTagString aria-hidden="true" />
            <SafariTagBody
              type="button"
              onClick={() => navigate('/halloween-belvoir-castle')}
              aria-label="Belvoir is cursed… Belvoir's Enchanted Halloween Trail"
            >
              <SafariTagPaw aria-hidden="true">
                <FontAwesomeIcon icon={faHatWizard} />
              </SafariTagPaw>
              Belvoir is cursed…
              <SafariTagDismiss
                role="button"
                aria-label="Dismiss"
                onClick={(e) => {
                  e.stopPropagation();
                  sessionStorage.setItem('safariTeaserDismissed', '1');
                  setSafariTeaserDismissed(true);
                }}
              >
                ×
              </SafariTagDismiss>
            </SafariTagBody>
          </SafariTagWrap>
        )}
      </NavbarContainer>
      <ChristmasMouse
        logoRef={mouseLogoRef}
        menuOpen={isOpen}
        enabled={currentSection !== 'duchess' && currentSection !== 'farmshop'}
      />
    </ThemeProvider>
  );
};

export default Navbar;
